import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import _ from "lodash";
import { Flex } from "reflexbox";
import { Field } from "../../UI/forms";
import Button from "../../UI/Button";
import { useTheme } from "styled-components";
import states from "../../../modules/states";
import { Body } from "../../UI/Text";
import ConfiguratorContext from "../../../context/configurator/ConfiguratorContext";
import * as r from "../../../requests/addressManagementRequests";
import { updateCustomer } from "../../../requests/customerManagementRequests";
import AlertContext from "../../../context/alert/AlertContext";
const { createAddress, updateAddress } = r;

const AddressForm = ({
  formToShow,
  setFormToShow,
  setEditingId,
  editingId,
}) => {
  const theme = useTheme();
  const { values } = useFormikContext();
  const { quote, setQuote } = useContext(ConfiguratorContext);
  const { showSuccessAlert, showFailureAlert } = useContext(AlertContext);

  const stateOptions = useMemo(
    () =>
      states.getStates().map(({ name, abbreviation: a }) => (
        <option value={a} key={a}>
          {name}
        </option>
      )),
    []
  );
  
  const onSubmitContact = useCallback(
    async (values) => {
      try {
        const customer = await updateCustomer({
          id: editingId,
          input: {
            contact: {
              ...values,
            },
          },
        });

        const { contact } = customer || {};

        if (contact) {
          return showSuccessAlert("Successfully updated Customer Contact");
        } else return showFailureAlert("Failed to update Customer Contact.");
      } catch (error) {
        return showFailureAlert(error);
      }
    },
    [editingId, showFailureAlert, showSuccessAlert]
  );

  const onSubmitAddress = useCallback(
    async (values) => {
      if (!editingId) {
        
        const newAddress = await createAddress({
          input: {
            ...values,
            AddressType: 2,
            customerId: quote?.customer?.id,
          },
        });
        let _quote = { ...quote };
        switch (formToShow) {
          case "Billing":
            _quote.billingAddress = newAddress;
            break;
          case "Shipping":
            _quote.shippingAddress = newAddress;
            break;
          default:
            const adds = [...quote?.shippingAddresses];
            adds.push(newAddress);
            _quote.shippingAddresses = [...adds];
            break;
        }
        setFormToShow("");
        setEditingId(null);
        return setQuote(_quote);
      } else {
        const updatedAddress = await updateAddress({
          id: editingId,
          input: { ...values, customerId: quote?.customer?.id },
        });
        let _quote = { ...quote };

        switch (formToShow) {
          case "Billing":
            _quote.billingAddress = updatedAddress;
            break;
          case "Shipping":
            _quote.shippingAddress = updatedAddress;
            break;
          default:
            const adds = [...quote?.shippingAddresses];
            const index = _.findIndex(adds, ({ id }) => id === editingId);
            _.remove(adds, ({ id }) => id === editingId);
            adds.splice(index, 0, updatedAddress);
            _quote.shippingAddresses = [...adds];
            if (quote.shippingAddressId === editingId)
              _quote.shippingAddress = updatedAddress;
            break;
        }

        setQuote(_quote);

        setFormToShow("");
        setEditingId(null);
      }
    },
    [editingId, formToShow, quote, setQuote, setEditingId, setFormToShow]
  );

  const handleKeys = useCallback(
    (e) => {
      if ((e.code === "Escape" || e.keyCode === 27) && !!formToShow) {
        setFormToShow("");
        setEditingId(null);
      }
      if (e.key === "Enter" && !!formToShow) {
        onSubmitAddress(values.address);
        setFormToShow("");
        setEditingId(null);
      }
    },
    [formToShow, onSubmitAddress, setEditingId, setFormToShow, values.address]
  );

  useEffect(() => {
    if (formToShow) {
      document.addEventListener("keydown", (e) => handleKeys(e));
      return () => {
        window.removeEventListener("keydown", (e) => handleKeys(e));
      };
    }
  }, [handleKeys, formToShow]);

  return (
    <>
      {["address", "Billing", "Shipping"].includes(formToShow) ? (
        <Flex flexDirection="column" m="0 auto" maxWidth="500px" w={1}>
          <Field name="address.Address" labelText="Address Line 1" />
          <Field name="address.Address2" labelText="Address Line 2" />
          <Field name="address.City" labelText="City" />
          <Field as="select" name="address.State" labelText="State">
            <option value="" key="default">
              Select a State
            </option>
            {stateOptions}
          </Field>
          <Field name="address.Zip" labelText="Zip" />
          <Field name="address.Country" labelText="Country" />
          <Flex width={1} justifyContent="flex-end">
            <Button
              reverse
              title="Cancel"
              color={theme.gray}
              iconName="faXmark"
              onClick={() => {
                setFormToShow("");
                setEditingId(null);
              }}
            />
            <Button
              title="Save"
              color={theme.success}
              iconName="faFloppyDisk"
              onClick={() => onSubmitAddress(values.address)}
            />
          </Flex>
        </Flex>
      ) : formToShow === "customer" ? (
        <Flex flexDirection="column" m="0 auto" maxWidth="500px" w={1}>
          {/* <Field name="contact.firstName" labelText="First Name" />
          <Field name="contact.lastName" labelText="Last Name" /> */}
          <Field name="contact.email" labelText="Email" />
          <Field name="contact.phone" labelText="Phone" phone />
          <Flex width={1} justifyContent="flex-end">
            <Button
              reverse
              title="Cancel"
              color={theme.gray}
              iconName="faXmark"
              onClick={() => {
                setFormToShow("");
                setEditingId(null);
              }}
            />
            <Button
              title="Save"
              color={theme.success}
              iconName="faFloppyDisk"
              onClick={() => onSubmitContact(values.contact)}
            />
          </Flex>
        </Flex>
      ) : (
        <>
          <Body>{values.shipping.Address}</Body>
          <Body>{values.shipping.Address2}</Body>
          <Body>{`${values.shipping.City}, ${values.shipping.State} ${values.shipping.Zip}`}</Body>
          <Body>{values.shipping.State}</Body>
          <Body>{}</Body>
          <Flex width={1} mt="1rem" justifyContent="flex-end">
            <Button
              title="Create Shipping Address"
              color={theme.secondary}
              iconName="faPencil"
              onClick={(e) => {
                e.stopPropagation();
                setEditingId(null);
                setFormToShow("address");
              }}
            />
          </Flex>
        </>
      )}
    </>
  );
};

export default AddressForm;
